package com.isaias.fyattravel.web.pages.checkout

import androidx.compose.runtime.*
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import com.isaias.fyattravel.web.data.WebProduct
import com.isaias.fyattravel.web.theme.ThemeColors
import org.w3c.fetch.RequestInit
import org.w3c.fetch.Headers

private const val FUNCTIONS_BASE = "https://us-central1-fyattravelnew.cloudfunctions.net"
private const val FYAT_WHATSAPP = "+256758250625"

/**
 * Checkout Page with PesaPal and Cash payment options
 */
@Composable
fun CheckoutPage(
    productId: String,
    onNavigate: (String) -> Unit,
) {
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var product by remember { mutableStateOf<WebProduct?>(null) }

    // Form fields
    var fullName by remember { mutableStateOf("") }
    var email by remember { mutableStateOf("") }
    var phone by remember { mutableStateOf("") }
    var notes by remember { mutableStateOf("") }
    
    // Payment states
    var isSubmitting by remember { mutableStateOf(false) }
    var isLoadingPesapal by remember { mutableStateOf(false) }
    var submitted by remember { mutableStateOf(false) }
    var selectedCurrency by remember { mutableStateOf("USD") }

    // Load product
    LaunchedEffect(productId) {
        if (productId.isBlank()) {
            error = "No product selected"
            loading = false
            return@LaunchedEffect
        }

        val bodyJson = """{"id":"${productId}"}"""

        window.fetch(
            "$FUNCTIONS_BASE/getProductById",
            RequestInit(
                method = "POST",
                headers = Headers().apply {
                    append("Content-Type", "application/json")
                },
                body = bodyJson
            )
        ).then { resp ->
            resp.text().then { text ->
                try {
                    val obj = kotlin.js.JSON.parse<dynamic>(text)
                    val ok = (obj.success as Boolean?) == true
                    if (!ok) {
                        error = (obj.message as String?) ?: "Failed to load product"
                    } else {
                        val d = obj.product
                        product = WebProduct(
                            id = d.id as String? ?: productId,
                            title = d.title as String?,
                            description = d.description as String?,
                            thumbnail = d.thumbnail as String?,
                            category = d.category as String?,
                            createdAt = (d.createdAt as Double?)?.toLong(),
                            priceUsd = (d.priceUsd as Double?)?.toInt(),
                            priceUgx = (d.priceUgx as Double?)?.toInt(),
                        )
                    }
                } catch (t: dynamic) {
                    error = "Invalid response from server"
                } finally {
                    loading = false
                }
            }
        }.catch { _ ->
            error = "Network error"
            loading = false
        }
    }

    // Calculate amount based on selected currency
    val amount = when (selectedCurrency) {
        "USD" -> product?.priceUsd ?: 0
        "UGX" -> product?.priceUgx ?: 0
        else -> product?.priceUsd ?: 0
    }

    // Build WhatsApp message for cash payment
    fun buildWhatsAppMessage(): String {
        return buildString {
            appendLine("*NEW CASH PAYMENT REQUEST - FYAT TRAVEL & TOUR*")
            appendLine("==========================================")
            appendLine()
            appendLine("*PRODUCT DETAILS:*")
            appendLine("Product: ${product?.title}")
            appendLine("Category: ${product?.category}")
            product?.priceUsd?.let { appendLine("Price USD: $$it") }
            product?.priceUgx?.let { appendLine("Price UGX: $it") }
            appendLine()
            appendLine("*CUSTOMER DETAILS:*")
            appendLine("Name: $fullName")
            appendLine("Email: ${email.ifBlank { "Not provided" }}")
            appendLine("Phone: ${phone.ifBlank { "Not provided" }}")
            appendLine()
            appendLine("*BOOKING DETAILS:*")
            appendLine("Payment Method: Cash (Pay on delivery/at office)")
            if (notes.isNotBlank()) {
                appendLine()
                appendLine("*NOTES:*")
                appendLine(notes)
            }
            appendLine()
            appendLine("Please confirm this booking and provide payment instructions.")
            appendLine("Thank you!")
        }
    }

    // PesaPal Payment Function
    fun payWithPesaPal() {
        if (fullName.isBlank() || email.isBlank() || phone.isBlank()) {
            error = "Please fill in all required fields (Name, Email, and Phone are required)"
            return
        }

        if (amount <= 0) {
            error = "Invalid product price. Please contact support."
            return
        }

        isLoadingPesapal = true
        error = null

        // Split full name into first + last (Firebase function requires both separately)
        val parts = fullName.trim().split(" ")
        val firstName = parts.firstOrNull() ?: fullName
        val lastName = if (parts.size > 1) parts.drop(1).joinToString(" ") else "."

        // Get timestamp for unique order ID (Date.now() returns Double in Kotlin/JS)
        val timestamp = (js("Date.now()") as Double).toLong()

        // Use JSON.stringify via a JS object so special characters in title/name are safely escaped
        val orderObj = js("{}")
        orderObj["amount"] = amount
        orderObj["currency"] = selectedCurrency
        orderObj["email"] = email
        orderObj["firstName"] = firstName
        orderObj["lastName"] = lastName
        orderObj["phoneNumber"] = phone
        orderObj["countryCode"] = "UG"
        orderObj["description"] = "${product?.title ?: "Product"} - Booking"
        orderObj["orderId"] = "FYAT-WEB-$productId-$timestamp"
        val orderRequest = kotlin.js.JSON.stringify(orderObj)

        // Log request body to browser console for debugging
        console.log("[PesaPal] Sending request to: $FUNCTIONS_BASE/createPesapalOrder")
        console.log("[PesaPal] Request body:", orderRequest)

        window.fetch(
            "$FUNCTIONS_BASE/createPesapalOrder",
            RequestInit(
                method = "POST",
                headers = Headers().apply {
                    append("Content-Type", "application/json")
                },
                body = orderRequest
            )
        ).then { resp ->
            resp.text().then { text ->
                // Log the raw server response for debugging
                console.log("[PesaPal] Server response status:", resp.status)
                console.log("[PesaPal] Server response body:", text)
                try {
                    val obj = kotlin.js.JSON.parse<dynamic>(text)
                    val success = (obj.success as Boolean?) == true

                    if (success) {
                        val checkoutUrl = obj.checkoutUrl as? String
                        val trackingId = obj.trackingId as? String

                        if (!checkoutUrl.isNullOrBlank()) {
                            // Store details for the callback/success page
                            window.localStorage.setItem("pesapal_order_id", trackingId ?: "")
                            window.localStorage.setItem("pesapal_product_id", productId)
                            window.localStorage.setItem("pesapal_customer_name", fullName)

                            // Redirect browser to PesaPal hosted payment page
                            window.location.href = checkoutUrl
                        } else {
                            error = "Payment URL not received from server. Please try again."
                            isLoadingPesapal = false
                        }
                    } else {
                        error = (obj.message as? String) ?: "Payment initiation failed. Please try again."
                        isLoadingPesapal = false
                    }
                } catch (t: dynamic) {
                    error = "Unexpected response from payment server. Please try again."
                    isLoadingPesapal = false
                }
            }
        }.catch { err: dynamic ->
            // Log the real error to browser console so you can see it in DevTools
            console.error("[PesaPal] Fetch error:", err)
            console.error("[PesaPal] Error message:", err?.message)
            val msg = (err?.message as? String) ?: "Unknown error"
            error = "Payment request failed: $msg. Open browser DevTools console for details."
            isLoadingPesapal = false
        }
    }

    // Cash/WhatsApp Payment Function
    fun payWithCashWhatsApp() {
        if (fullName.isBlank()) {
            error = "Please enter your full name"
            return
        }
        if (email.isBlank() && phone.isBlank()) {
            error = "Please provide email or phone number"
            return
        }

        val message = buildWhatsAppMessage()
        val encoded = js("encodeURIComponent")(message) as String
        window.open("https://wa.me/${FYAT_WHATSAPP.replace("+", "")}?text=$encoded", "_blank")
        submitted = true
    }

    // Validation
    val isValid = fullName.isNotBlank() && email.isNotBlank() && phone.isNotBlank()

    // Back button
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(0.75.em)
            marginBottom(1.5.em)
        }
    }) {
        Button({
            style {
                backgroundColor(ThemeColors.surface)
                color(ThemeColors.textPrimary)
                padding(0.65.em, 1.em)
                borderRadius(0.75.em)
                property("border", "1px solid ${ThemeColors.divider}")
                cursor("pointer")
                fontWeight("700")
            }
            onClick { 
                product?.category?.let { onNavigate("/category/$it") } ?: onNavigate("/categories")
            }
        }) { Text("Back") }

        H1({
            style {
                fontSize(1.75.em)
                fontWeight("800")
                margin(0.px)
                color(ThemeColors.textPrimary)
            }
        }) { Text("Checkout") }
    }

    if (loading) {
        Div({
            style {
                textAlign("center")
                padding(3.em)
            }
        }) {
            Div({
                style {
                    width(48.px)
                    height(48.px)
                    borderRadius(50.percent)
                    property("border", "4px solid ${ThemeColors.divider}")
                    property("border-top-color", "${ThemeColors.primary}")
                    property("animation", "spin 1s linear infinite")
                    property("margin", "0 auto")
                    marginBottom(1.em)
                }
            }) {}
            P({ style { color(ThemeColors.textMuted) } }) { Text("Loading...") }
        }
        return
    }

    error?.let { msg ->
        Div({
            style {
                backgroundColor(ThemeColors.errorBg)
                color(ThemeColors.error)
                padding(1.em)
                borderRadius(0.75.em)
                marginBottom(1.em)
                fontWeight("600")
            }
        }) { Text(msg) }
    }

    val p = product
    if (p == null) {
        P({ style { color(ThemeColors.textMuted) } }) { Text("Product not found.") }
        return
    }

    if (submitted) {
        Div({
            style {
                backgroundColor(ThemeColors.successBg)
                color(ThemeColors.success)
                padding(2.em)
                borderRadius(1.em)
                textAlign("center")
            }
        }) {
            H2({ style { marginBottom(0.5.em) } }) { Text("Booking Request Received!") }
            P({ style { marginBottom(1.em) } }) { 
                Text("Thank you $fullName! We have received your booking request for ${p.title}. Our team will contact you shortly to confirm your reservation and provide payment instructions.") 
            }
            Button({
                style {
                    backgroundColor(ThemeColors.primary)
                    color(ThemeColors.textOnGold)
                    padding(0.75.em, 1.5.em)
                    borderRadius(0.75.em)
                    border(0.px)
                    cursor("pointer")
                    fontWeight("700")
                }
                onClick { onNavigate("/categories") }
            }) {
                Text("Browse More")
            }
        }
        return
    }

    // Loading overlay for PesaPal
    if (isLoadingPesapal) {
        Div({
            style {
                position(Position.Fixed)
                top(0.px)
                left(0.px)
                right(0.px)
                bottom(0.px)
                backgroundColor(ThemeColors.shadow)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                property("z-index", "1000")
            }
        }) {
            Div({
                style {
                    backgroundColor(ThemeColors.surface)
                    padding(2.em)
                    borderRadius(1.em)
                    textAlign("center")
                    property("box-shadow", "0 25px 50px ${ThemeColors.shadow}")
                }
            }) {
                Div({
                    style {
                        width(48.px)
                        height(48.px)
                        borderRadius(50.percent)
                    property("border", "4px solid ${ThemeColors.divider}")
                    property("border-top-color", "${ThemeColors.primary}")
                        property("animation", "spin 1s linear infinite")
                        property("margin", "0 auto")
                        marginBottom(1.em)
                    }
                }) {}
                H3({
                    style {
                        fontSize(1.25.em)
                        fontWeight("700")
                        color(ThemeColors.textPrimary)
                        marginBottom(0.5.em)
                    }
                }) { Text("Preparing Payment...") }
                P({ style { color(ThemeColors.textMuted) } }) { Text("Please wait while we connect to PesaPal") }
            }
        }
    }

    // Main checkout layout
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "1fr 1fr")
            gap(2.em)
            property("max-width", "900px")
            property("margin", "0 auto")
            property("@media (max-width: 900px)", "{ grid-template-columns: 1fr; gap: 1.25em; }")
        }
    }) {
        // Left column - Product summary
        Div({
            style {
                backgroundColor(ThemeColors.surface)
                borderRadius(1.em)
                padding(1.5.em)
                property("border", "1px solid ${ThemeColors.divider}")
                property("box-shadow", "0 2px 10px rgba(0,0,0,0.04)")
                height(100.percent)
            }
        }) {
            H2({
                style {
                    fontSize(1.25.em)
                    fontWeight("700")
                    marginBottom(1.em)
                    color(ThemeColors.textPrimary)
                }
            }) { Text("Order Summary") }

            // Product image
            p.thumbnail?.let { url ->
                if (url.isNotBlank()) {
                    Img(src = url, attrs = {
                        style {
                            width(100.percent)
                            height(200.px)
                            borderRadius(0.75.em)
                            property("object-fit", "cover")
                            marginBottom(1.em)
                        }
                    })
                }
            }

            // Product title
            H3({
                style {
                    fontSize(1.1.em)
                    fontWeight("700")
                    marginBottom(0.5.em)
                    color(ThemeColors.textPrimary)
                }
            }) { Text(p.title ?: "Product") }

            // Category badge
            p.category?.let { cat ->
                Span({
                    style {
                        backgroundColor(ThemeColors.primaryMuted)
                        color(ThemeColors.primary)
                        padding(0.25.em, 0.75.em)
                        borderRadius(1.em)
                        fontSize(0.85.em)
                        fontWeight("600")
                    }
                }) { Text(cat) }
            }

            // Description
            p.description?.let { desc ->
                if (desc.isNotBlank()) {
                    P({
                        style {
                            color(ThemeColors.textMuted)
                            fontSize(0.9.em)
                            marginTop(1.em)
                            marginBottom(1.em)
                        }
                    }) { Text(desc) }
                }
            }

            // Currency selector
            Div({
                style {
                    marginTop(1.em)
                    marginBottom(1.em)
                }
            }) {
                Label(null, {
                    style {
                        display(DisplayStyle.Block)
                        fontSize(0.9.em)
                        fontWeight("600")
                        color(ThemeColors.textMuted)
                        marginBottom(0.5.em)
                    }
                }) { Text("Select Currency") }
                
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        gap(0.5.em)
                    }
                }) {
                    listOf("USD", "UGX").forEach { currency ->
                        val isSelected = selectedCurrency == currency
                        Button({
                            style {
                                padding(0.5.em, 1.em)
                                borderRadius(0.5.em)
                                border(0.px)
                                cursor("pointer")
                                fontWeight("600")
                                backgroundColor(if (isSelected) ThemeColors.primary else ThemeColors.skeleton)
                                color(if (isSelected) ThemeColors.textOnGold else ThemeColors.textMuted)
                            }
                            onClick { selectedCurrency = currency }
                        }) { Text(currency) }
                    }
                }
            }

            // Price display
            val priceParts = listOfNotNull(
                p.priceUsd?.let { "USD $it" },
                p.priceUgx?.let { "UGX $it" }
            )
            if (priceParts.isNotEmpty()) {
                Div({
                    style {
                        backgroundColor(ThemeColors.successBg)
                        padding(1.em)
                        borderRadius(0.75.em)
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.SpaceBetween)
                        marginTop(1.em)
                    }
                }) {
                    Span({
                        style {
                            color(ThemeColors.success)
                            fontWeight("600")
                            fontSize(0.9.em)
                        }
                    }) { Text("Total Amount") }
                    Span({
                        style {
                            color(ThemeColors.success)
                            fontWeight("800")
                            fontSize(1.5.em)
                        }
                    }) { 
                        when (selectedCurrency) {
                            "USD" -> Text(p.priceUsd?.let { "USD $$it" } ?: "Contact for price")
                            "UGX" -> Text(p.priceUgx?.let { "UGX $it" } ?: "Contact for price")
                            else -> Text(priceParts.joinToString(" • "))
                        }
                    }
                }
            }
        }

        // Right column - Booking form
        Div({
            style {
                backgroundColor(ThemeColors.surface)
                borderRadius(1.em)
                padding(1.5.em)
                property("border", "1px solid ${ThemeColors.divider}")
                property("box-shadow", "0 2px 10px rgba(0,0,0,0.04)")
            }
        }) {
            H2({
                style {
                    fontSize(1.25.em)
                    fontWeight("700")
                    marginBottom(1.em)
                    color(ThemeColors.textPrimary)
                }
            }) { Text("Your Details") }

            // Form fields
            Div({ style { marginBottom(1.em) } }) {
                Label(null, {
                    style {
                        display(DisplayStyle.Block)
                        fontSize(0.9.em)
                        fontWeight("600")
                    color(ThemeColors.textMuted)
                        marginBottom(0.375.em)
                    }
                }) { Text("Full Name *") }
                Input(InputType.Text) {
                    style {
                        width(100.percent)
                        padding(0.75.em)
                        borderRadius(0.5.em)
                        property("border", "1.5px solid #333333")
                        backgroundColor(ThemeColors.surface)
                        fontSize(1.em)
                    }
                    attr("placeholder", "Enter your full name")
                    value(fullName)
                    onInput { fullName = it.value }
                }
            }

            Div({
                style {
                    display(DisplayStyle.Grid)
                    property("grid-template-columns", "1fr 1fr")
                    gap(1.em)
                    marginBottom(1.em)
                    property("@media (max-width: 640px)", "{ grid-template-columns: 1fr; }")
                }
            }) {
                Div {
                    Label(null, {
                        style {
                            display(DisplayStyle.Block)
                            fontSize(0.9.em)
                            fontWeight("600")
                            color(ThemeColors.textMuted)
                            marginBottom(0.375.em)
                        }
                    }) { Text("Email *") }
                    Input(InputType.Email) {
                        style {
                            width(100.percent)
                            padding(0.75.em)
                            borderRadius(0.5.em)
                            property("border", "1.5px solid ${ThemeColors.divider}")
                            backgroundColor(ThemeColors.surface)
                            fontSize(1.em)
                        }
                        attr("placeholder", "your@email.com")
                        value(email)
                        onInput { email = it.value }
                    }
                }

                Div {
                    Label(null, {
                        style {
                            display(DisplayStyle.Block)
                            fontSize(0.9.em)
                            fontWeight("600")
                            color(ThemeColors.textMuted)
                            marginBottom(0.375.em)
                        }
                    }) { Text("Phone *") }
                    Input(InputType.Tel) {
                        style {
                            width(100.percent)
                            padding(0.75.em)
                            borderRadius(0.5.em)
                            property("border", "1.5px solid ${ThemeColors.divider}")
                            backgroundColor(ThemeColors.surface)
                            fontSize(1.em)
                        }
                        attr("placeholder", "+256...")
                        value(phone)
                        onInput { phone = it.value }
                    }
                }
            }

            Div({ style { marginBottom(1.25.em) } }) {
                Label(null, {
                    style {
                        display(DisplayStyle.Block)
                        fontSize(0.9.em)
                        fontWeight("600")
                            color(ThemeColors.textMuted)
                        marginBottom(0.375.em)
                    }
                }) { Text("Additional Notes") }
                TextArea(notes) {
                    style {
                        width(100.percent)
                        padding(0.75.em)
                        borderRadius(0.5.em)
                            property("border", "1.5px solid ${ThemeColors.divider}")
                            backgroundColor(ThemeColors.surface)
                        fontSize(1.em)
                        height(100.px)
                        property("resize", "vertical")
                    }
                    attr("placeholder", "Any special requests or additional information...")
                    onInput { notes = it.value }
                }
            }

            // Payment Options
            H3({
                style {
                    fontSize(1.em)
                    fontWeight("700")
                    marginTop(1.em)
                    marginBottom(0.75.em)
                        color(ThemeColors.textPrimary)
                }
            }) { Text("Select Payment Method") }

            // PesaPal Payment Button
            Button({
                style {
                    width(100.percent)
                    backgroundColor(if (isValid && !isLoadingPesapal) ThemeColors.primary else ThemeColors.primaryMuted)
                    color(ThemeColors.textOnGold)
                    padding(1.em)
                    borderRadius(0.75.em)
                    border(0.px)
                    cursor(if (isValid && !isLoadingPesapal) "pointer" else "not-allowed")
                    fontWeight("700")
                    fontSize(1.em)
                    property("box-shadow", if (isValid) "0 4px 12px rgba(37,99,235,0.3)" else "none")
                    marginBottom(0.75.em)
                }
                onClick { 
                    if (isValid && !isLoadingPesapal) {
                        payWithPesaPal()
                    }
                }
            }) {
                if (isLoadingPesapal) {
                    Text("Processing...")
                } else {
                    Text("Pay with PesaPal (Card/Mobile Money)")
                }
            }

            // Payment method logos
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.Center)
                    alignItems(AlignItems.Center)
                    gap(0.5.em)
                    marginBottom(1.em)
                    padding(0.5.em)
                    backgroundColor(ThemeColors.surface)
                    borderRadius(0.5.em)
                }
            }) {
                Span({ style { fontSize(0.8.em); color(ThemeColors.textMuted) } }) { Text("Pay with:") }
                Span({ style { fontWeight("600"); fontSize(0.85.em) } }) { Text("Visa") }
                Span({ style { fontWeight("600"); fontSize(0.85.em) } }) { Text("•") }
                Span({ style { fontWeight("600"); fontSize(0.85.em) } }) { Text("Mastercard") }
                Span({ style { fontWeight("600"); fontSize(0.85.em) } }) { Text("•") }
                Span({ style { fontWeight("600"); fontSize(0.85.em) } }) { Text("Mobile Money") }
            }

            // Cash/WhatsApp Button
            Button({
                style {
                    width(100.percent)
                    backgroundColor(if (isValid) ThemeColors.whatsapp else ThemeColors.primaryMuted)
                    color(ThemeColors.textOnDark)
                    padding(1.em)
                    borderRadius(0.75.em)
                    border(0.px)
                    cursor(if (isValid) "pointer" else "not-allowed")
                    fontWeight("700")
                    fontSize(1.em)
                    property("box-shadow", if (isValid) "0 4px 12px rgba(37,211,102,0.3)" else "none")
                }
                onClick { 
                    if (isValid) {
                        payWithCashWhatsApp()
                    }
                }
            }) {
                Text("Pay Cash (Contact via WhatsApp)")
            }

            // Payment note
            P({
                style {
                    color(ThemeColors.textMuted)
                    fontSize(0.85.em)
                    marginTop(1.em)
                    textAlign("center")
                }
            }) {
                Text("Choose PesaPal for instant online payment or WhatsApp to arrange cash payment directly with our team.")
            }
        }
    }
}
